import Konva from 'konva';
import { Layer } from 'konva/lib/Layer';

const GUIDE_COLOR  = '#FF0000';
const GUIDE_WIDTH  = 1;
const GUIDE_DASH   = [5, 5];
const GUIDE_OPACITY = 1;
const TICK_SIZE    = 6;   // half-length of endpoint tick marks

/**
 * Dedicated layer for rendering snap guide lines.
 * Responsibilities: draw connecting guides, clear guides, batchDraw.
 * No snap calculations or object iteration belong here.
 */
export default class SnapLayer extends Layer {
  _guides = [];

  constructor() {
    super({ draggable: false, listening: false });
  }

  /**
   * Draw a vertical alignment guide at absolute stage x coordinate.
   * The guide spans only between yMin and yMax (connecting the two objects).
   * Tick marks are drawn at both ends.
   */
  drawVerticalGuide(x, yMin, yMax, color = GUIDE_COLOR) {
    this._add(this._line([x, yMin, x, yMax], color));
    this._add(this._tick([x - TICK_SIZE, yMin, x + TICK_SIZE, yMin], color));
    this._add(this._tick([x - TICK_SIZE, yMax, x + TICK_SIZE, yMax], color));
  }

  /**
   * Draw a horizontal alignment guide at absolute stage y coordinate.
   * The guide spans only between xMin and xMax.
   * Tick marks are drawn at both ends.
   */
  drawHorizontalGuide(y, xMin, xMax, color = GUIDE_COLOR) {
    this._add(this._line([xMin, y, xMax, y], color));
    this._add(this._tick([xMin, y - TICK_SIZE, xMin, y + TICK_SIZE], color));
    this._add(this._tick([xMax, y - TICK_SIZE, xMax, y + TICK_SIZE], color));
  }

  /** Remove all active guides and redraw. */
  clearGuides() {
    this._guides.forEach(n => n.destroy());
    this._guides = [];
    this.batchDraw();
  }

  /** Flush all pending guide draws to the canvas. */
  flushGuides() {
    this.batchDraw();
  }

  _add(node) {
    this.add(node);
    this._guides.push(node);
    return node;
  }

  /**
   * Draw a guide line through (cx, cy) perpendicular to the segment (segDx, segDy),
   * plus a small tick along the segment itself — used for midpoint snap indicators.
   */
  /**
   * Draw a guide from the segment midpoint (cx, cy) to the cursor (cursorX, cursorY),
   * plus a small tick along the segment at the midpoint to show orientation.
   */
  drawPerpendicularGuide(cx, cy, segDx, segDy, cursorX, cursorY, color = GUIDE_COLOR) {
    const segLen = Math.hypot(segDx, segDy);
    if (segLen === 0) return;
    const tx = segDx / segLen;
    const ty = segDy / segLen;
    this._add(this._line([cx, cy, cursorX, cursorY], color));
    this._add(this._tick([
      cx - tx * TICK_SIZE, cy - ty * TICK_SIZE,
      cx + tx * TICK_SIZE, cy + ty * TICK_SIZE,
    ], color));
  }

  _line(points, color = GUIDE_COLOR) {
    return new Konva.Line({
      points,
      stroke: color,
      strokeWidth: GUIDE_WIDTH,
      dash: GUIDE_DASH,
      opacity: GUIDE_OPACITY,
      listening: false,
      perfectDrawEnabled: false,
    });
  }

  _tick(points, color = GUIDE_COLOR) {
    return new Konva.Line({
      points,
      stroke: color,
      strokeWidth: GUIDE_WIDTH,
      opacity: GUIDE_OPACITY,
      listening: false,
      perfectDrawEnabled: false,
    });
  }
}
